import { useState, useEffect, useCallback } from 'react';
import { useCatalog } from './useCatalog';
import type {
  DescriptionData,
  MeasurementData,
  KeywordData,
  ProvenanceRecord,
  AdditionalInfoData,
  ExhibitionData
} from '../contexts/CatalogContext';

interface ReviewData {
  catalogItem: Record<string, unknown> | null;
  description: DescriptionData | null;
  measurements: MeasurementData | null;
  keywords: KeywordData | null;
  provenance: ProvenanceRecord[] | null;
  additionalInfo: AdditionalInfoData | null;
  exhibitions: { exhibitions: ExhibitionData[]; stepNotes: Record<string, string[]> } | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

interface UseReviewDataOptions {
  /**
   * Milliseconds to wait before making the initial fetch on mount.
   *
   * When navigating to the Review tab, the departing tab fires fire-and-forget
   * flush saves. Without a delay, the Review tab's GET requests race against
   * those in-flight POSTs and may see stale/empty data (#1060).
   *
   * Defaults to 0 (fetch immediately) to preserve backward compatibility.
   * Set to e.g. 300 when used in the Review tab of the wizard.
   */
  mountDelay?: number;
}

const getValue = <T,>(result: PromiseSettledResult<T>): T | null =>
  result.status === 'fulfilled' ? result.value : null;

export const useReviewData = (
  catalogItemId: string | undefined,
  ownerId?: string | null,
  options?: UseReviewDataOptions
): ReviewData => {
  const mountDelay = options?.mountDelay ?? 0;

  const {
    getCatalogItem,
    getDescription,
    getMeasurements,
    getKeywords,
    getProvenanceChain,
    getAdditionalInfo,
    getExhibitions
  } = useCatalog();

  const [state, setState] = useState<Omit<ReviewData, 'refetch'>>({
    catalogItem: null,
    description: null,
    measurements: null,
    keywords: null,
    provenance: null,
    additionalInfo: null,
    exhibitions: null,
    loading: false,
    error: null,
  });
  const [fetchTrigger, setFetchTrigger] = useState(0);

  useEffect(() => {
    if (!catalogItemId) return;

    let cancelled = false;
    let delayTimer: ReturnType<typeof setTimeout> | null = null;

    const doFetch = () => {
      if (cancelled) return;

      // Set loading state synchronously before async work
      setState(prev => ({ ...prev, loading: true, error: null }));

      Promise.allSettled([
        getCatalogItem(catalogItemId, ownerId),
        getDescription(catalogItemId, ownerId),
        getMeasurements(catalogItemId, ownerId),
        getKeywords(catalogItemId, ownerId),
        getProvenanceChain(catalogItemId, ownerId),
        getAdditionalInfo(catalogItemId, ownerId),
        getExhibitions(catalogItemId, ownerId)
      ]).then(results => {
        if (cancelled) return;

        const hasAnyFailure = results.some(r => r.status === 'rejected');

        setState({
          catalogItem: getValue(results[0]) as Record<string, unknown> | null,
          description: getValue(results[1]) as DescriptionData | null,
          measurements: getValue(results[2]) as MeasurementData | null,
          keywords: getValue(results[3]) as KeywordData | null,
          provenance: (getValue(results[4]) as { records?: ProvenanceRecord[] } | null)?.records ?? null,
          additionalInfo: getValue(results[5]) as AdditionalInfoData | null,
          exhibitions: getValue(results[6]) as { exhibitions: ExhibitionData[]; stepNotes: Record<string, string[]> } | null,
          loading: false,
          error: hasAnyFailure ? 'Failed to load some review data' : null,
        });
      });
    };

    if (mountDelay > 0) {
      delayTimer = setTimeout(doFetch, mountDelay);
    } else {
      doFetch();
    }

    return () => {
      cancelled = true;
      if (delayTimer !== null) {
        clearTimeout(delayTimer);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogItemId, ownerId, fetchTrigger]);

  const refetch = useCallback(() => {
    setFetchTrigger(prev => prev + 1);
  }, []);

  return {
    ...state,
    refetch
  };
};
